import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Activity, FilePenLine, FileText, SearchX, Send, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { isApiError } from '../../api/client';
import { receiptsApi } from '../../api/endpoints';
import { activityCodesQuery, organizationQuery, queryKeys, receiptQuery } from '../../api/queries';
import type { ReceiptDetail, TimelineEvent } from '../../api/types';
import { useAccess } from '../../app/access';
import { TestEnvStrip } from '../../app/shell/TestEnvStrip';
import { useI18n } from '../../i18n/I18nProvider';
import { UserText } from '../../ui/Bidi';
import { ConfirmDialog } from '../../ui/Dialog';
import { Badge, Callout, EmptyState, Skeleton } from '../../ui/Feedback';
import { Money, Page, Panel, useDocumentTitle } from '../../ui/Layout';
import { QueryError } from '../../ui/QueryError';
import { useToast } from '../../ui/Toast';
import { LIVE_STATES, MockLabel } from '../sales/status';
import styles from './Receipt.module.css';
import {
  isReturnWithReference,
  isReturnWithoutReference,
  isSaleType,
  issuableTotals,
} from './receipts';
import {
  ReceiptEtaBadge,
  ReceiptStageBadge,
  cairoDateOf,
  usePaymentMethodLabel,
  useReceiptTypeLabel,
  useTaxTypeName,
} from './ReceiptStatus';

/**
 * The receipt's header as the reader should see it. An issued receipt's columns are its own; a
 * draft keeps what the user typed in its draft payload until issue (the row's columns are filled
 * then), so a draft is read from there (WP-8.9c): nothing typed is shown as missing.
 */
function headerOf(d: ReceiptDetail) {
  const r = d.receipt;
  const p = r.status === 'draft' ? d.draft : null;
  return {
    issueDate: r.issueDateLocal ?? p?.issueDate ?? null,
    exchangeRate: r.exchangeRate ?? p?.exchangeRate ?? null,
    activityCode: r.activityCode ?? p?.activityCode ?? null,
    paymentMethod: r.paymentMethod ?? p?.paymentMethod ?? null,
    buyerType: p?.buyer?.type ?? r.buyerType,
    buyerId: r.buyerId ?? p?.buyer?.id ?? null,
    buyerName: r.buyerName ?? p?.buyer?.name ?? null,
    documentUseReason: r.documentUseReason ?? p?.documentUseReason ?? null,
    salesIssuedDateTime: r.salesIssuedDateTime ?? p?.salesIssuedDateTime ?? null,
    referenceUuid: r.referenceUuid ?? p?.referenceUUID ?? null,
  };
}

/**
 * One e-receipt, and the issue step of MF-RCP-003 (MF-RCP-004 is the no-reference return; the
 * competitor's receipt page was never observed, d03:32): the values, the lines, the tax totals, the
 * receipt uuid and the pointer to the previous receipt on the same POS device, and the live ETA
 * status with its reason in words. A draft shows its amounts as the issue will compute them
 * (WP-8.9c) and is issued after a confirmation that names the number, the device, the environment
 * and the total. An issued receipt is read-only; an invalid one offers a corrected receipt, a valid
 * sale a return.
 */
export function ReceiptDetailPage() {
  const { t, formatNumber, pick } = useI18n();
  const { orgId = '', receiptId = '' } = useParams({ strict: false }) as {
    orgId?: string;
    receiptId?: string;
  };
  const access = useAccess(orgId);
  const toast = useToast();
  const queryClient = useQueryClient();
  const typeLabel = useReceiptTypeLabel();
  const taxName = useTaxTypeName();
  const paymentLabel = usePaymentMethodLabel();
  const org = useQuery(organizationQuery(orgId));
  const activities = useQuery(activityCodesQuery(orgId));
  const [confirming, setConfirming] = useState(false);
  const q = useQuery({
    ...receiptQuery(orgId, receiptId),
    refetchInterval: (qq) => {
      const s = qq.state.data?.eta?.status;
      return s && LIVE_STATES.has(s) ? 1500 : s === 'valid' ? 4000 : false;
    },
  });
  const d = q.data;
  useDocumentTitle(
    d?.receipt.receiptNumber ? t.receipts.detail.title(d.receipt.receiptNumber) : t.receipts.title,
  );
  const reference = useQuery({
    queryKey: [
      ...queryKeys.org(orgId),
      'receipts',
      'by-uuid',
      d ? headerOf(d).referenceUuid : null,
    ],
    queryFn: async ({ signal }) =>
      (
        await receiptsApi.list(
          orgId,
          { uuid: (d && headerOf(d).referenceUuid) ?? '', limit: 1 },
          signal,
        )
      ).items[0] ?? null,
    enabled: Boolean(d && headerOf(d).referenceUuid),
  });

  const issue = useMutation({
    mutationFn: (version: number) => receiptsApi.issue(orgId, receiptId, version),
    onSuccess: async (res) => {
      setConfirming(false);
      toast.success(t.receipts.detail.issuedToast(res.receiptNumber));
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'receipts'] });
    },
    onError: (error) => {
      setConfirming(false);
      toast.error(error);
    },
  });

  if (q.isError) {
    if (isApiError(q.error) && q.error.status === 404)
      return (
        <Page narrow>
          <Panel>
            <EmptyState
              icon={<SearchX size={22} />}
              title={t.receipts.detail.notFoundTitle}
              action={
                <Link to="/orgs/$orgId/sales/receipts" params={{ orgId }}>
                  {t.receipts.editor.backToList}
                </Link>
              }
            >
              <span data-testid="receipt-not-found">{t.receipts.detail.notFoundBody}</span>
            </EmptyState>
          </Panel>
        </Page>
      );
    return (
      <Page>
        <QueryError error={q.error} onRetry={() => void q.refetch()} />
      </Page>
    );
  }
  if (!d)
    return (
      <Page>
        <Skeleton height="12rem" />
      </Page>
    );

  const r = d.receipt;
  const h = headerOf(d);
  const isDraft = r.status === 'draft';
  const canEdit = isDraft && access.can('receipt.create');
  const environment = org.data?.etaEnvironment;
  const preview = isDraft ? d.preview : null;
  const draftTotals = issuableTotals(preview);
  const etaStatus = d.eta?.status ?? null;
  const canCorrect = !isDraft && etaStatus === 'invalid' && access.can('receipt.create');
  const canReturn =
    !isDraft && etaStatus === 'valid' && isSaleType(r.receiptType) && access.can('receipt.create');
  const activity = activities.data?.find((a) => a.code === h.activityCode);

  return (
    <Page>
      <section className={styles.hero}>
        <div className={styles.heroRow}>
          <div className={styles.heroText}>
            <span className={styles.heroEyebrow}>
              <FileText size={14} aria-hidden="true" />
              <Link
                to="/orgs/$orgId/sales/receipts"
                params={{ orgId }}
                style={{ color: 'inherit' }}
              >
                {t.receipts.title}
              </Link>
            </span>
            <h1 className={styles.heroTitle} data-testid="detail-title">
              {r.receiptNumber ?? t.receipts.title}
            </h1>
            <div className={styles.headerBadges}>
              <ReceiptStageBadge value={r.status} testId="detail-stage" />
              {/* A draft has no ETA state yet: no placeholder chip (RB3R8-26). */}
              {!isDraft ? (
                <>
                  <ReceiptEtaBadge value={etaStatus} testId="detail-eta" />
                  <MockLabel environment={d.eta?.environment} />
                </>
              ) : null}
              <Badge tone="neutral" className={styles.typeBadge}>
                {typeLabel(r.receiptType)}
              </Badge>
            </div>
          </div>
          <div className={styles.heroActions}>
            {canEdit ? (
              <Link
                to="/orgs/$orgId/sales/receipts/$receiptId/edit"
                params={{ orgId, receiptId }}
                className={styles.textButton}
                data-testid="edit-draft"
              >
                {t.receipts.editDraft}
              </Link>
            ) : null}
            {isDraft && access.can('receipt.submit') ? (
              <button
                type="button"
                className={styles.primaryButton}
                data-testid="issue-send"
                disabled={issue.isPending}
                onClick={() => setConfirming(true)}
              >
                <Send size={16} aria-hidden="true" />
                {issue.isPending ? t.receipts.detail.issuing : t.receipts.issueSend}
              </button>
            ) : null}
            {canCorrect ? (
              <Link
                to="/orgs/$orgId/sales/receipts/new"
                params={{ orgId }}
                search={{ from: r.id }}
                className={styles.primaryButton}
                data-testid="start-correction"
              >
                <FilePenLine size={16} aria-hidden="true" />
                {t.receipts.detail.correctAction}
              </Link>
            ) : null}
            {canReturn ? (
              <Link
                to="/orgs/$orgId/sales/receipts/new"
                params={{ orgId }}
                search={{ returnOf: r.id }}
                className={styles.textButton}
                data-testid="start-return"
              >
                <Undo2 size={16} aria-hidden="true" />
                {t.receipts.detail.returnAction}
              </Link>
            ) : null}
          </div>
        </div>
      </section>

      {/* Issue and send is irreversible: confirm it as the invoice page does (WP-8.9 D-2), with the
          amounts the issue will compute (WP-8.9c, RB3R8-02). */}
      <ConfirmDialog
        isOpen={confirming}
        onOpenChange={(open) => !open && setConfirming(false)}
        title={t.receipts.confirm.title(r.receiptNumber ?? '')}
        confirmLabel={t.receipts.issueSend}
        confirmVariant="primary"
        isPending={issue.isPending}
        onConfirm={() => issue.mutate(r.version)}
      >
        <TestEnvStrip compact />
        <p>{t.receipts.confirm.body}</p>
        <dl className={styles.kv} data-testid="receipt-issue-confirm">
          <dt>{t.receipts.detail.number}</dt>
          <dd className={styles.mono} data-testid="receipt-confirm-number">
            {r.receiptNumber ?? '—'}
          </dd>
          <dt>{t.receipts.detail.pos}</dt>
          <dd data-testid="receipt-confirm-device">
            {d.device ? (
              <>
                <UserText>{d.device.name}</UserText>{' '}
                <bdi dir="ltr" className={styles.mono}>
                  ({d.device.serial})
                </bdi>
              </>
            ) : (
              <bdi dir="ltr" className={styles.mono}>
                {r.posDeviceId}
              </bdi>
            )}
          </dd>
          <dt>{t.receipts.detail.etaEnvironment}</dt>
          <dd data-testid="receipt-confirm-environment">
            {environment ? t.env[environment] : '—'} <MockLabel environment={environment} />
          </dd>
          {draftTotals && preview ? (
            <>
              {/* Each tax type as the server computed it, then the receipt total: no sum here. */}
              {preview.taxTotals.map((tax) => (
                <TaxConfirmRow
                  key={tax.taxType}
                  name={taxName(tax.taxType)}
                  amount={tax.amount}
                  currency={preview.currency}
                />
              ))}
              <dt className={styles.totalTerm}>{t.receipts.detail.totalAmount}</dt>
              <dd>
                <Money
                  value={draftTotals.totalAmount}
                  currency={preview.currency}
                  testId="receipt-confirm-total"
                />
              </dd>
            </>
          ) : (
            <>
              <dt>{t.receipts.detail.totalAmount}</dt>
              <dd data-testid="receipt-confirm-total-missing">{t.receipts.confirm.noAmounts}</dd>
            </>
          )}
        </dl>
      </ConfirmDialog>

      {!isDraft ? (
        <Callout tone="info" role="note">
          {t.receipts.detail.readOnly}
        </Callout>
      ) : (
        <Callout tone="warning" role="note">
          <span data-testid="draft-note">
            {draftTotals ? t.receipts.detail.draftNote : t.receipts.detail.draftNoAmounts}
          </span>
        </Callout>
      )}

      <section className={styles.card} data-testid="detail-values">
        <h2 className={styles.cardTitle}>{t.receipts.detail.valuesTitle}</h2>
        <dl className={styles.kv}>
          <dt>{t.receipts.detail.number}</dt>
          <dd className={styles.mono} data-testid="detail-number">
            {r.receiptNumber ?? '—'}
          </dd>
          <dt>{t.receipts.detail.type}</dt>
          <dd>{typeLabel(r.receiptType)}</dd>
          <dt>{t.receipts.detail.issueDate}</dt>
          <dd data-testid="detail-issue-date">
            {h.issueDate ? <DateValue value={h.issueDate} /> : '—'}
          </dd>
          {r.dateTimeIssued ? (
            <>
              <dt>{t.receipts.detail.issuedAt}</dt>
              <dd>
                <DateTimeValue value={r.dateTimeIssued} />
              </dd>
            </>
          ) : null}
          <dt>{t.receipts.detail.currency}</dt>
          <dd className={styles.mono}>{r.currency}</dd>
          {r.currency !== 'EGP' ? (
            <>
              <dt>{t.receipts.detail.exchangeRate}</dt>
              <dd className={styles.num} data-testid="detail-rate">
                {h.exchangeRate ? formatNumber(h.exchangeRate) : '—'}
              </dd>
            </>
          ) : null}
          <dt>{t.receipts.detail.activity}</dt>
          <dd data-testid="detail-activity">
            {h.activityCode ? (
              <>
                <span className={styles.mono}>{h.activityCode}</span>
                {activity ? <> — {pick(activity.nameAr, activity.nameEn)}</> : null}
              </>
            ) : (
              '—'
            )}
          </dd>
          <dt>{t.receipts.detail.pos}</dt>
          {/* The device name is text (any script); only its serial or identifier is mono. */}
          <dd data-testid="detail-pos">
            {d.device ? (
              <>
                <UserText>{d.device.name}</UserText>{' '}
                <bdi dir="ltr" className={styles.mono}>
                  ({d.device.serial})
                </bdi>
              </>
            ) : (
              <bdi dir="ltr" className={styles.mono}>
                {r.posDeviceId}
              </bdi>
            )}
          </dd>
          <dt>{t.receipts.detail.branch}</dt>
          <dd>
            {d.branch ? (
              <>
                <UserText>{d.branch.name}</UserText>{' '}
                <bdi dir="ltr" className={styles.mono}>
                  ({d.branch.etaBranchId})
                </bdi>
              </>
            ) : (
              '—'
            )}
          </dd>
          <dt>{t.receipts.detail.paymentMethod}</dt>
          <dd data-testid="detail-payment">{paymentLabel(h.paymentMethod)}</dd>
          <dt>{t.receipts.detail.buyerType}</dt>
          <dd>{t.receipts.editor.buyerTypes[h.buyerType]}</dd>
          <dt>{t.receipts.detail.buyerId}</dt>
          <dd className={styles.mono} data-testid="detail-buyer-id">
            {h.buyerId ?? '—'}
          </dd>
          <dt>{t.receipts.detail.buyerName}</dt>
          <dd data-testid="detail-buyer-name">
            {h.buyerName ? <UserText>{h.buyerName}</UserText> : '—'}
          </dd>
          {isReturnWithoutReference(r.receiptType) ? (
            <>
              <dt>{t.receipts.detail.documentUseReason}</dt>
              <dd>
                {h.documentUseReason
                  ? t.receipts.editor.useReasons[h.documentUseReason as 'I' | 'B']
                  : '—'}
              </dd>
              <dt>{t.receipts.detail.salesIssuedDateTime}</dt>
              {/* Entered as a date only: shown as a date, never with a time (RB3R8-19). */}
              <dd data-testid="detail-sale-date">
                {h.salesIssuedDateTime ? (
                  <DateValue value={cairoDateOf(h.salesIssuedDateTime)} />
                ) : (
                  '—'
                )}
              </dd>
            </>
          ) : null}
          {isReturnWithReference(r.receiptType) ? (
            <>
              <dt>{t.receipts.detail.referenceReceipt}</dt>
              <dd data-testid="detail-reference">
                {reference.data ? (
                  <Link
                    to="/orgs/$orgId/sales/receipts/$receiptId"
                    params={{ orgId, receiptId: reference.data.id }}
                    className={styles.numberLink}
                  >
                    {reference.data.receiptNumber}
                  </Link>
                ) : (
                  '—'
                )}
              </dd>
              <dt>{t.receipts.detail.referenceUuid}</dt>
              <dd className={styles.mono}>{h.referenceUuid ?? '—'}</dd>
            </>
          ) : null}
          <dt>{t.receipts.detail.uuid}</dt>
          <dd className={styles.mono} data-testid="detail-uuid">
            {r.uuid ?? '—'}
          </dd>
          <dt>{t.receipts.detail.previousUuid}</dt>
          <dd data-testid="detail-previous-uuid">
            {r.previousUuid ? (
              <span className={styles.mono}>{r.previousUuid}</span>
            ) : (
              <span className={styles.subtle}>
                {isDraft ? t.receipts.detail.previousAtIssue : t.receipts.detail.previousNone}
              </span>
            )}
          </dd>
          {d.chain ? (
            <>
              <dt>{t.receipts.detail.chainHead}</dt>
              <dd className={styles.mono} data-testid="detail-chain-head">
                {d.chain.lastReceiptNumber} · {d.chain.lastUuid}
              </dd>
            </>
          ) : null}
          <dt>{t.receipts.detail.createdAt}</dt>
          <dd>
            <DateTimeValue value={r.createdAt} />
          </dd>
        </dl>
      </section>

      <section className={styles.card} data-testid="detail-lines-section">
        <h2 className={styles.cardTitle}>{t.receipts.detail.linesTitle}</h2>
        {isDraft ? <DraftLines detail={d} /> : <IssuedLines detail={d} />}
      </section>

      {(isDraft ? (preview?.taxTotals.length ?? 0) > 0 && draftTotals : d.taxTotals.length) ? (
        <section className={styles.card} data-testid="detail-tax-totals">
          <h2 className={styles.cardTitle}>{t.receipts.detail.taxesTitle}</h2>
          <table className={styles.kvTable}>
            <thead>
              <tr>
                <th>{t.receipts.detail.taxType}</th>
                <th className={styles.end}>{t.receipts.detail.taxAmount}</th>
              </tr>
            </thead>
            <tbody>
              {(isDraft ? (preview?.taxTotals ?? []) : d.taxTotals).map((tax) => (
                <tr key={tax.taxType}>
                  <td>{taxName(tax.taxType)}</td>
                  <td className={styles.end}>
                    <Money value={tax.amount} currency={r.currency} className={styles.num} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {!isDraft || draftTotals ? (
        <section className={styles.card} data-testid="detail-totals">
          <h2 className={styles.cardTitle}>{t.receipts.detail.totalsTitle}</h2>
          <dl className={styles.kv}>
            <dt>{t.receipts.detail.totalSales}</dt>
            <dd>
              <Money
                value={isDraft ? draftTotals?.totalSales : r.totals.totalSales}
                currency={r.currency}
                className={styles.num}
              />
            </dd>
            <dt>{t.receipts.detail.netAmount}</dt>
            <dd>
              <Money
                value={isDraft ? draftTotals?.netAmount : r.totals.netAmount}
                currency={r.currency}
                className={styles.num}
              />
            </dd>
            <dt className={styles.totalTerm}>{t.receipts.detail.totalAmount}</dt>
            <dd>
              <Money
                value={isDraft ? draftTotals?.totalAmount : r.totals.totalAmount}
                currency={r.currency}
                testId="detail-total"
                className={styles.num}
              />
            </dd>
          </dl>
        </section>
      ) : null}

      {!isDraft && d.eta ? (
        <section className={styles.card} data-testid="detail-eta-panel">
          <h2 className={styles.cardTitle}>{t.receipts.detail.etaTitle}</h2>
          <dl className={styles.kv}>
            <dt>{t.receipts.detail.etaStatus}</dt>
            <dd>
              <ReceiptEtaBadge value={d.eta.status} />
              {refusedAtSubmission(d) ? (
                <span className={styles.subtle} data-testid="detail-not-accepted">
                  {' '}
                  — {t.receipts.detail.notAcceptedQualifier}
                </span>
              ) : null}
            </dd>
            <dt>{t.receipts.detail.etaReason}</dt>
            <dd data-testid="detail-eta-reason">
              <EtaReason detail={d} />
            </dd>
            <dt>{t.receipts.detail.etaEnvironment}</dt>
            <dd>{t.env[d.eta.environment]}</dd>
            <dt>{t.receipts.detail.etaUuid}</dt>
            <dd className={styles.mono}>{d.eta.etaUuid ?? '—'}</dd>
            <dt>{t.receipts.detail.etaSyncedAt}</dt>
            <dd>{d.eta.lastSyncedAt ? <DateTimeValue value={d.eta.lastSyncedAt} /> : '—'}</dd>
          </dl>
        </section>
      ) : null}

      <section className={styles.card} data-testid="detail-history">
        <h2 className={styles.cardTitle}>
          <span className={styles.cardIcon} aria-hidden="true">
            <Activity size={16} />
          </span>
          {t.receipts.detail.historyTitle}
        </h2>
        <ul className={styles.timeline}>
          {d.events.map((event) => (
            <li key={event.seq} data-event={event.event}>
              <span className={styles.timelineAt}>
                <DateTimeValue value={event.at} />
              </span>
              <span className={styles.timelineEvent}>{eventLabel(t, event)}</span>
              {historyReason(t, event) ? (
                <span className={styles.subtle} data-reason={event.reason}>
                  {historyReason(t, event)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </Page>
  );
}

function TaxConfirmRow({
  name,
  amount,
  currency,
}: {
  name: string;
  amount: string;
  currency: string;
}) {
  return (
    <>
      <dt>{name}</dt>
      <dd>
        <Money value={amount} currency={currency} testId="receipt-confirm-tax" />
      </dd>
    </>
  );
}

/** Refused at submission: ETA stores the receipt as Invalid, and the history says so. */
function refusedAtSubmission(d: ReceiptDetail): boolean {
  return d.events.some((e) => e.event === 'eta_submission_rejected');
}

interface ParsedReason {
  code: string | null;
  message: string | null;
  raw: string | null;
}

/** The stored reason: a JSON refusal (`{code, message}`) or plain text; never shown raw as such. */
function parseReason(reason: string | null): ParsedReason {
  if (!reason) return { code: null, message: null, raw: null };
  try {
    const value: unknown = JSON.parse(reason);
    if (value && typeof value === 'object') {
      const o = value as { code?: unknown; message?: unknown };
      return {
        code: typeof o.code === 'string' ? o.code : null,
        message: typeof o.message === 'string' ? o.message : null,
        raw: JSON.stringify(value, null, 2),
      };
    }
  } catch {
    /* plain text */
  }
  return { code: null, message: reason, raw: reason };
}

/**
 * The Tax Authority's verdict in words (WP-8.9c, RB3R8-05/06): one sentence for the state, one per
 * known code, the failing check's name, and the raw answer only inside a closed "technical
 * details" block — never raw JSON as the reason.
 */
function EtaReason({ detail }: { detail: ReceiptDetail }) {
  const { t } = useI18n();
  const v = t.receipts.detail;
  const eta = detail.eta;
  if (!eta) return <>—</>;
  const reason = parseReason(eta.statusReason);
  const steps = (eta.validationResults?.validationSteps ?? []).filter(
    (s) => s.status && s.status.toLowerCase() === 'invalid',
  );
  const codes = [
    ...new Set(
      [reason.code, ...steps.map((s) => s.error?.errorCode ?? null)].filter((c): c is string =>
        Boolean(c),
      ),
    ),
  ];
  const known = codes.filter((c) => v.reasonCodes[c]);
  const lead =
    eta.status === 'invalid'
      ? refusedAtSubmission(detail)
        ? v.reasonNotAccepted
        : v.reasonInvalid
      : eta.status === 'cancelled'
        ? v.reasonCancelled
        : LIVE_STATES.has(eta.status)
          ? v.reasonLive
          : null;
  // The raw text: the stored reason, the failed steps, and any event reason the history does not
  // put in words (U-3: never raw machine text in the reading, always available here).
  const technical = [
    ...new Set(
      [
        reason.raw,
        ...steps.map((s) =>
          [s.name, s.error?.errorCode, s.error?.error].filter(Boolean).join(' · '),
        ),
        ...detail.events
          .filter(isEtaEvent)
          .map((e) => e.reason)
          .filter((x): x is string => Boolean(x) && !v.eventReasons[x as string]),
      ].filter((x): x is string => Boolean(x)),
    ),
  ];
  if (!lead && technical.length === 0) return <>—</>;
  return (
    <div>
      {lead ? <p className={styles.reasonLead}>{lead}</p> : null}
      {known.length || steps.length ? (
        <ul className={styles.reasonList}>
          {steps.map((s, i) => (
            <li key={`step-${i}`}>
              {v.reasonStep} <UserText>{s.name ?? '—'}</UserText>
            </li>
          ))}
          {known.map((c) => (
            <li key={c} data-code={c}>
              {v.reasonCodes[c]}
            </li>
          ))}
        </ul>
      ) : codes.length && (eta.status === 'invalid' || eta.status === 'cancelled') ? (
        <p className={styles.subtle}>{v.reasonUnknownCode}</p>
      ) : null}
      {eta.status === 'invalid' ? <p className={styles.subtle}>{v.correctHint}</p> : null}
      {technical.length ? (
        <details className={styles.techDetails} data-testid="detail-eta-technical">
          <summary>{v.techDetails}</summary>
          <pre className={styles.techBody} dir="ltr">
            {technical.join('\n\n')}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

/**
 * What the history says beside an event. Our own events keep their note (a draft's number); a Tax
 * Authority event says a known reason in words and never the raw text the Tax Authority (or the
 * mock) sent, which the folded technical details keep (U-3).
 */
function historyReason(t: ReturnType<typeof useI18n>['t'], event: TimelineEvent): string | null {
  if (!event.reason) return null;
  const known = t.receipts.detail.eventReasons[event.reason];
  if (known) return known;
  return isEtaEvent(event) ? null : event.reason;
}

const isEtaEvent = (event: TimelineEvent) => event.event.startsWith('eta_');

function eventLabel(t: ReturnType<typeof useI18n>['t'], event: TimelineEvent): string {
  return t.receipts.detail.events[event.event] ?? event.event;
}

function DateValue({ value }: { value: string }) {
  const { formatDate } = useI18n();
  return <span data-value={value}>{formatDate(value)}</span>;
}

function DateTimeValue({ value }: { value: string }) {
  const { formatDateTime } = useI18n();
  return <span data-value={value}>{formatDateTime(value)}</span>;
}

/** A quantity, trimmed (2, not 2.00000), never broken inside its digits. */
function Quantity({ value }: { value: string | null | undefined }) {
  const { formatNumber } = useI18n();
  return <span className={styles.num}>{value ? formatNumber(value) : '—'}</span>;
}

/** A unit price, 2 to 5 decimals, never broken inside its digits. */
function Price({ value }: { value: string | null | undefined }) {
  const { formatAmount } = useI18n();
  return <span className={styles.num}>{value ? formatAmount(value) : '—'}</span>;
}

/** A draft: the lines as typed, with each line's total as the issue will compute it. */
function DraftLines({ detail }: { detail: ReceiptDetail }) {
  const { t } = useI18n();
  const lines = detail.draft?.lines ?? [];
  const totals = issuableTotals(detail.preview) ? (detail.preview?.lines ?? null) : null;
  const currency = detail.receipt.currency;
  if (!lines.length) return <p className={styles.subtle}>{t.receipts.editor.noLines}</p>;
  return (
    <table className={`${styles.lineTable} ${styles.viewLines}`} data-testid="detail-lines">
      <caption className="visually-hidden">{t.receipts.detail.linesTitle}</caption>
      <thead>
        <tr>
          <th>#</th>
          <th>{t.receipts.editor.item}</th>
          <th className={styles.end}>{t.receipts.editor.quantity}</th>
          <th className={styles.end}>{t.receipts.editor.unitPrice(currency)}</th>
          {totals ? <th className={styles.end}>{t.receipts.detail.lineTotal}</th> : null}
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => (
          <tr key={index} data-testid={`detail-line-${index + 1}`}>
            <td>{index + 1}</td>
            <td>{line.description ? <UserText>{line.description}</UserText> : '—'}</td>
            <td className={styles.end}>
              <span className={styles.lineLabel} aria-hidden="true">
                {t.receipts.editor.quantity}
              </span>
              <Quantity value={line.quantity} />
            </td>
            <td className={styles.end}>
              <span className={styles.lineLabel} aria-hidden="true">
                {t.receipts.editor.unitPrice(currency)}
              </span>
              <Price value={line.unitPrice} />
            </td>
            {totals ? (
              <td className={styles.end}>
                <span className={styles.lineLabel} aria-hidden="true">
                  {t.receipts.detail.lineTotal}
                </span>
                <Money value={totals[index]?.total} currency={currency} className={styles.num} />
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** An issued receipt: the server's lines with their computed totals. */
function IssuedLines({ detail }: { detail: ReceiptDetail }) {
  const { t } = useI18n();
  const currency = detail.receipt.currency;
  return (
    <table className={`${styles.lineTable} ${styles.viewLines}`} data-testid="detail-lines">
      <caption className="visually-hidden">{t.receipts.detail.linesTitle}</caption>
      <thead>
        <tr>
          <th>#</th>
          <th>{t.receipts.editor.item}</th>
          <th className={styles.end}>{t.receipts.editor.quantity}</th>
          <th className={styles.end}>{t.receipts.editor.unitPrice(currency)}</th>
          <th className={styles.end}>{t.receipts.detail.lineTotal}</th>
        </tr>
      </thead>
      <tbody>
        {detail.lines.map((line) => (
          <tr key={line.lineNo} data-testid={`detail-line-${line.lineNo}`}>
            <td>{line.lineNo}</td>
            <td>
              <div className={styles.lineName}>
                <UserText>{line.description}</UserText>
              </div>
              <div className={styles.subtle}>
                <bdi dir="ltr">
                  {line.itemType} {line.itemCode}
                </bdi>
              </div>
            </td>
            <td className={styles.end}>
              <span className={styles.lineLabel} aria-hidden="true">
                {t.receipts.editor.quantity}
              </span>
              <Quantity value={line.quantity} />
            </td>
            <td className={styles.end}>
              <span className={styles.lineLabel} aria-hidden="true">
                {t.receipts.editor.unitPrice(currency)}
              </span>
              <Price value={line.unitPrice} />
            </td>
            <td className={styles.end}>
              <span className={styles.lineLabel} aria-hidden="true">
                {t.receipts.detail.lineTotal}
              </span>
              <Money value={line.total} currency={currency} className={styles.num} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
